import { hasCoreAttackStyle } from './coreAttackStyles';
import { getDie, isValidDieRoll } from './dice';
import { getBoardEncounterRound } from './runConstants';

export const POWER_UP_CHARGE_SECONDS = 1.5;
export const POWER_UP_BLAST_SECONDS = 0.45;
export const MAX_POWER_UP_MULTIPLIER = 10;
export const getPowerUpMultiplier = (effect) =>
  Number.isInteger(effect?.multiplier)
    ? Math.min(MAX_POWER_UP_MULTIPLIER, Math.max(1, effect.multiplier))
    : 1;
export const getPowerUpBonusPercent = (multiplier = 1) =>
  20 * getPowerUpMultiplier({ multiplier });
export const boostPowerUpRoll = (roll, multiplier = 1) =>
  Math.round(roll * (1 + getPowerUpBonusPercent(multiplier) / 100));

export const upgradePowerUpTile = (square) => {
  const { powerUpSpent, ...tile } = square || {};
  return {
    ...tile,
    modifier: 0,
    effect: {
      type: 'powerUp',
      multiplier: Math.min(
        MAX_POWER_UP_MULTIPLIER,
        getPowerUpMultiplier(tile.effect) + 1
      ),
    },
  };
};

export const getPowerUpMultipliers = (powerUp) =>
  powerUp?.multipliers || Array(powerUp?.boosts || 0).fill(1);

export const isValidPowerUpFaces = (faces) =>
  Array.isArray(faces) &&
  faces.length === 6 &&
  faces.every((value) => Number.isSafeInteger(value) && value >= 0);

export const getPowerUpFaceValues = (
  baseFaces,
  powerUp,
  { useSavedFaces = true } = {}
) =>
  useSavedFaces && isValidPowerUpFaces(powerUp?.faceValues)
    ? [...powerUp.faceValues]
    : baseFaces.map((value) =>
        getPowerUpMultipliers(powerUp).reduce(
          (powered, multiplier) => boostPowerUpRoll(powered, multiplier),
          value
        )
      );

// Recover the base face of a physical landing using the exact boost history,
// including rounding at each activation. Chaos has 100 possible base values.
export const getPowerUpNaturalRoll = (roll, die, powerUp, candidates) => {
  const baseFaces =
    candidates ||
    getDie(die).faces ||
    Array.from({ length: 100 }, (_, index) => index + 1);
  const poweredFaces = getPowerUpFaceValues(baseFaces, powerUp, {
    useSavedFaces: false,
  });
  return baseFaces[poweredFaces.indexOf(roll)] ?? null;
};

export const getPowerUpRequest = (game) => {
  const state = game.boardTileResolution;
  return state?.floor === game.floor &&
    state.landingIndex === game.landingIndex &&
    state.round === getBoardEncounterRound(game) &&
    state.pending?.type === 'powerUp'
    ? state.pending
    : null;
};

const getGambitRelaunchRequest = (game) => {
  const state = game.boardTileResolution;
  return state?.floor === game.floor &&
    state.landingIndex === game.landingIndex &&
    state.round === getBoardEncounterRound(game) &&
    state.pending?.type === 'gambit' &&
    state.pending.relaunch === true
    ? state.pending
    : null;
};

export const getBoardRelaunchRequest = (game) =>
  getPowerUpRequest(game) || getGambitRelaunchRequest(game);

export const isValidPoweredRoll = (roll, die, powerUp, candidates) => {
  const validBase = (value) =>
    candidates ? candidates.includes(value) : isValidDieRoll(value, die);
  if (!powerUp) return validBase(roll);
  if (
    !validBase(powerUp.naturalRoll) ||
    !Number.isSafeInteger(powerUp.boosts) ||
    powerUp.boosts < 1 ||
    (powerUp.multipliers === undefined
      ? powerUp.boosts > 25
      : !Array.isArray(powerUp.multipliers) ||
        powerUp.multipliers.length !== powerUp.boosts)
  )
    return false;
  let expected = powerUp.naturalRoll;
  for (const multiplier of getPowerUpMultipliers(powerUp)) {
    if (
      !Number.isInteger(multiplier) ||
      multiplier < 1 ||
      multiplier > MAX_POWER_UP_MULTIPLIER
    )
      return false;
    expected = boostPowerUpRoll(expected, multiplier);
    if (!Number.isSafeInteger(expected)) return false;
  }
  return roll === expected;
};

export const getRelaunchLanding = (
  board,
  sourceIndex,
  random = Math.random
) => {
  const options = (board || [])
    .map((_, index) => index)
    .filter((index) => index !== sourceIndex);
  return options.length
    ? options[
        Math.min(options.length - 1, Math.floor(random() * options.length))
      ]
    : sourceIndex;
};

// Preserve the layout and progress of runs saved with the retired tiles.
export const migratePowerUpTiles = (game) => {
  const replace = (square) =>
    square?.powerUpSpent
      ? upgradePowerUpTile(square)
      : ['capacitor', 'recycler'].includes(square?.effect?.type)
      ? { ...square, effect: { type: 'powerUp' } }
      : square;
  const resolution = game.boardTileResolution;
  const pending = resolution?.pending;
  const fixedFaces = hasCoreAttackStyle(game)
    ? null
    : getDie(game.selectedDie).faces;
  return {
    ...game,
    doorBoard: game.doorBoard?.map(replace) ?? game.doorBoard,
    ...(fixedFaces &&
    game.powerUp &&
    isValidPoweredRoll(game.roll, game.selectedDie, game.powerUp)
      ? {
          powerUp: {
            ...game.powerUp,
            faceValues: getPowerUpFaceValues(fixedFaces, game.powerUp, {
              useSavedFaces: false,
            }),
          },
        }
      : {}),
    ...(resolution
      ? {
          boardTileResolution: {
            ...resolution,
            entries: resolution.entries.map((entry) => ({
              ...entry,
              square: replace(entry.square),
            })),
            pending:
              pending?.type === 'recycler'
                ? {
                    id: `${pending.id}:powerUp`,
                    type: 'powerUp',
                    multiplier: 1,
                    index: pending.index,
                    before: game.roll,
                    after: boostPowerUpRoll(game.roll),
                    options: [],
                  }
                : pending,
          },
        }
      : {}),
  };
};
